import * as api from '../../api';
import {
  handleError,
  receiveUsers,
  checkTemporaryTokenValidity,
  retryOperation,
} from '../actionHelpers';
import { fetchSchool, fetchSchoolLicenses } from '../schools/actions';
import {
  getTokenFromCookie,
  setCookie,
  getCookie,
  setTokenCookie,
} from '../../utilities';
import { getNormalizedErrorMessage } from '../../errors';
import {
  getInvitedTeachersResults,
  getTeachersForActiveSchool,
  getListById,
  getHasUIHintBeenSeen,
} from '../reducers';
import { setSentryUser, clearSentryUser } from 'Utils/errorTracking';

export const fetchUser = (id) => (dispatch, getState) => {
  dispatch({
    type: 'FETCH_USER_REQUEST',
    id,
  });

  const { token } = getState().users.auth;
  return api.getUser(id, token).then(
    (response) => {
      if (response.errorMsg) {
        handleError(dispatch, response, 'FETCH_USER_FAILURE');
      } else {
        dispatch(receiveUsers(response, 'FETCH_USER_SUCCESS'));
      }
    },
    (error) => handleError(dispatch, error, 'FETCH_USER_FAILURE')
  );
};

export const checkCookieForAuthToken = () => (dispatch) => {
  dispatch({
    type: 'CHECK_COOKIE_FOR_AUTH_TOKEN',
    token: getTokenFromCookie(),
  });
};

export const setAuthToken = (token) => (dispatch) => {
  dispatch({
    type: 'SET_AUTH_TOKEN',
    token,
  });
};

export const checkCookieForActiveSchool = () => (dispatch) => {
  dispatch({
    type: 'CHECK_COOKIE_FOR_ACTIVE_SCHOOL',
    schoolId: getCookie('tbsActiveSchool'),
  });
};

/**
 * UI Hints can be set multiple times, but it’s really designed
 * to be a boolean. So we check to see if the value has already
 * been set before trying to set it again. It’s not a big deal if
 * it gets sent again, but we want to avoid unnecessary network requests.
 * @param {string} key
 */
export const setUIHintForActiveUser = (key) => (dispatch, getState) => {
  if (getHasUIHintBeenSeen(getState(), key)) {
    // console.log('oh, we already hinted.', key);
    return false;
  }

  dispatch({
    type: 'POST_UI_HINT_REQUEST',
  });

  const { token } = getState().users.auth;
  return api.setUIHint(key, token).then(
    (response) => {
      if (response.errorMsg) {
        handleError(dispatch, response, 'POST_UI_HINT_FAILURE');
      } else {
        dispatch({
          type: 'POST_UI_HINT_SUCCESS',
          hintKey: key,
        });
      }
    },
    (error) => handleError(dispatch, error, 'POST_UI_HINT_FAILURE')
  );
};

export const fetchActiveUser = (newToken = null) => (dispatch, getState) => {
  const id = 'me';
  const state = getState();
  const token = newToken || state.users.auth.token;

  if (newToken) dispatch(setAuthToken(newToken));
  dispatch({
    type: 'FETCH_ACTIVE_USER_REQUEST',
  });
  return api.getUser(id, token).then(
    (response) => {
      if (response.errorMsg) {
        handleError(dispatch, response, 'FETCH_ACTIVE_USER_FAILURE');
      } else {
        const activeUserAction = receiveUsers(
          response,
          'FETCH_ACTIVE_USER_SUCCESS'
        );
        dispatch(activeUserAction);
        const existingSchoolId = state.users.auth.schoolId;
        const newActiveUser =
          activeUserAction.response.entities.users[
            activeUserAction.response.result
          ];
        console.log('NEW ACTIVE USER', newActiveUser);
        const activeSchoolId = existingSchoolId || newActiveUser.roles[0][0];
        dispatch(fetchSchool(activeSchoolId));
        dispatch(fetchSchoolLicenses(activeSchoolId));

        if (newActiveUser) {
          setSentryUser({
            id: newActiveUser.id,
            email: newActiveUser.auth_info.username,
            schoolId: activeSchoolId,
          });
        }
      }
    },
    (error) => handleError(dispatch, error, 'FETCH_ACTIVE_USER_FAILURE')
  );
};

export const changeActiveSchool = (id) => (dispatch, getState) => {
  dispatch({
    type: 'CHANGE_ACTIVE_SCHOOL',
    schoolId: id,
  });

  // Set a cookie for the active school that expires when the auth token does
  const expirationDate = new Date(getState().users.auth.tokenExpiration);
  setCookie('tbsActiveSchool', id.toString(), expirationDate);
};

export const changeEmailSubscriptions = (token, changeset) => (dispatch) => {
  dispatch({
    type: 'CHANGE_EMAIL_SUBSCRIPTIONS_REQUEST',
  });

  return api.patchEmailSubcriptions(changeset, token).then(
    (response) => {
      if (response.errorMsg) {
        handleError(dispatch, response, 'CHANGE_EMAIL_SUBSCRIPTIONS_FAILURE');
      } else {
        dispatch({
          type: 'CHANGE_EMAIL_SUBSCRIPTIONS_SUCCESS',
          changeset,
        });
      }
    },
    (error) =>
      handleError(dispatch, error, 'CHANGE_EMAIL_SUBSCRIPTIONS_FAILURE')
  );
};

export const fetchActiveUserIfNeeded = () => (dispatch, getState) => {
  const state = getState();
  if (!state.users.activeUser.user) dispatch(fetchActiveUser());
};

export const fetchStudentsInClass = (classId) => (dispatch, getState) => {
  dispatch({
    type: 'FETCH_STUDENTS_IN_CLASS_REQUEST',
    classId,
  });
  const { token } = getState().users.auth;
  return api.getStudentsInClass(classId, token).then(
    (response) => {
      if (response.errorMsg) {
        handleError(dispatch, response, 'FETCH_STUDENTS_IN_CLASS_FAILURE');
      } else {
        dispatch(
          receiveUsers(response, 'FETCH_STUDENTS_IN_CLASS_SUCCESS', { classId })
        );
      }
    },
    (error) => handleError(dispatch, error, 'FETCH_STUDENTS_IN_CLASS_FAILURE')
  );
};

export const fetchStudentsInClassIfNeeded = (id) => (dispatch, getState) => {
  const state = getState();
  const classReducer = getListById(state, `students_in_class_${id}`);
  if (!classReducer || (classReducer && !classReducer.isFetching))
    dispatch(fetchStudentsInClass(id));
};

const fetchUsersForSchool = (endpoint, name) => (dispatch, getState) => {
  const state = getState();
  const { token, schoolId } = state.users.auth;
  dispatch({
    type: `FETCH_USER_GROUP_REQUEST`,
    name,
    schoolId,
  });
  return endpoint(schoolId, token).then(
    (response) => {
      if (response.errorMsg) {
        handleError(dispatch, response, `FETCH_USER_GROUP_FAILURE`, { name });
      } else {
        dispatch(
          receiveUsers(response, `FETCH_USER_GROUP_SUCCESS`, { schoolId, name })
        );
      }
    },
    (error) =>
      handleError(dispatch, error, `FETCH_USER_GROUP_FAILURE`, { name })
  );
};

export const fetchTeachersForSchool = () => (dispatch) => {
  return dispatch(
    fetchUsersForSchool(api.getTeachersForSchool, 'teachers_in_school')
  );
};

export const fetchStudentsForSchool = () => (dispatch) => {
  return dispatch(
    fetchUsersForSchool(api.getStudentsForSchool, 'students_in_school')
  );
};

export const postUserLogout = (options) => (dispatch, getState) => {
  // Lol, so apparently we don’t need to do this!
  dispatch({
    type: 'POST_LOGOUT_REQUEST',
  });

  if (typeof window.Beacon !== 'undefined') {
    window.Beacon('logout');
  }

  const { token } = getState().users.auth;
  return api.logout(token).then(
    (response) => {
      if (response.errorMsg) {
        handleError(dispatch, response, 'POST_LOGOUT_FAILURE', {});
      } else {
        clearSentryUser();
        dispatch({
          type: 'POST_LOGOUT_SUCCESS',
          message:
            options && options.expiredToken
              ? getNormalizedErrorMessage('errors.expired_auth_token')
              : undefined,
        });
      }
    },
    (error) => handleError(dispatch, error, 'POST_LOGOUT_FAILURE')
  );
};

export const deactivateUser = (userId) => (dispatch, getState) => {
  const state = getState();
  const { schoolId, token } = state.users.auth;
  dispatch({
    type: 'DEACTIVATE_USER_REQUEST',
    id: userId,
    schoolId,
  });

  return api.postDeactivateUser(schoolId, userId, token).then(
    (response) => {
      if (response.errorMsg) {
        handleError(dispatch, response, 'DEACTIVATE_USER_FAILURE');
      } else {
        dispatch({
          type: 'DEACTIVATE_USER_SUCCESS',
          id: userId,
          schoolId,
        });
      }
    },
    (error) => handleError(dispatch, error, 'DEACTIVATE_USER_FAILURE')
  );
};

export const removeTeacherInvite = (email, id) => (dispatch, getState) => {
  const state = getState();
  const { schoolId, token } = state.users.auth;
  dispatch({
    type: 'CLEAR_USER_GROUP_MESSAGE',
    name: 'teachers_in_school',
  });
  const extraArgs = {
    name: 'teachers_in_school',
    email,
    id,
    schoolId,
  };
  return api.deleteInvitedTeacher(email, schoolId, token).then(
    (response) => {
      if (response.errorMsg) {
        handleError(
          dispatch,
          response,
          'REMOVE_USER_FROM_USER_GROUP_FAILURE',
          extraArgs
        );
      } else {
        dispatch({
          type: 'REMOVE_USER_FROM_USER_GROUP',
          message: {
            type: 'success',
            msgId: 'users_list.message.invite_removed',
          },
          ...extraArgs,
        });
      }
    },
    (error) =>
      handleError(
        dispatch,
        error,
        'REMOVE_USER_FROM_USER_GROUP_FAILURE',
        extraArgs
      )
  );
};

export const resendTeacherInvite = (email, id) => (dispatch, getState) => {
  const state = getState();
  const { schoolId, token } = state.users.auth;
  dispatch({
    type: 'RESEND_TEACHER_INVITE_REQUEST',
    email,
    id,
    schoolId,
  });
  dispatch({
    type: 'CLEAR_USER_GROUP_MESSAGE',
    id: 'teachers_in_school',
  });

  return api.postInviteTeacher(email, schoolId, token).then(
    (response) => {
      if (response.errorMsg) {
        handleError(dispatch, response, 'RESEND_TEACHER_INVITE_FAILURE', {
          email,
          id,
          schoolId,
        });
      } else {
        dispatch({
          type: 'RESEND_TEACHER_INVITE_SUCCESS',
          email,
          id,
          schoolId,
        });
        dispatch({
          type: 'UPDATE_USER_GROUP_MESSAGE',
          id: 'teachers_in_school',
          message: {
            type: 'success',
            msgId: 'users_list.message.invite_resent',
          },
        });
      }
    },
    (error) =>
      handleError(dispatch, error, 'RESEND_TEACHER_INVITE_FAILURE', {
        email,
        id,
        schoolId,
      })
  );
};

export const inviteTeacher = (
  email,
  originalEmail,
  schoolId,
  token,
  dispatch,
  state
) => {
  dispatch({
    type: 'POST_INVITE_TEACHER_REQUEST',
    email,
    originalEmail,
    schoolId,
  });
  if (originalEmail) {
    // TODO: This doesn’t really work as intended. It correctly
    // rejects the request, but it somehow removes the existing email
    // from the success list.  It’s a visual bug, but we should fix it.
    const resultsByEmail = getInvitedTeachersResults(state);
    const previouslySubmittedEmails = Object.keys(resultsByEmail);
    const emailWasAlreadySubmitted = previouslySubmittedEmails.includes(email);
    if (emailWasAlreadySubmitted) {
      return handleError(
        dispatch,
        {
          type: 'error',
          msgId: 'errors.teacher_email_already_submitted',
          errorCode: 30,
        },
        'POST_INVITE_TEACHER_FAILURE',
        { email, originalEmail, schoolId }
      );
    }
  }
  return api.postInviteTeacher(email, schoolId, token).then(
    (response) => {
      if (response.errorMsg) {
        handleError(dispatch, response, 'POST_INVITE_TEACHER_FAILURE', {
          email,
          originalEmail,
          schoolId,
        });
      } else {
        const normalizedResponse = { results: [response.results] };
        // If you re-invite a teacher by entering an email address that is
        // already in the system, a new ID is generated. This means that we
        // have a duplicate row in the list of teachers until you refresh.
        // So, we’ll check to see if the email is tied to a user in the store.
        const allTeachers = getTeachersForActiveSchool(state);
        const filteredTeachers = allTeachers.filter(
          (teacher) => teacher.auth_info.username === email
        );
        const isDuplicate = filteredTeachers.length > 0;
        const inviteAction = receiveUsers(
          normalizedResponse,
          'POST_INVITE_TEACHER_SUCCESS',
          { email, originalEmail, schoolId, isDuplicate }
        );
        dispatch(inviteAction);
        dispatch({
          ...inviteAction,
          type: 'ADD_USER_TO_USER_GROUP',
          name: 'teachers_in_school',
        });
      }
    },
    (error) =>
      handleError(dispatch, error, 'POST_INVITE_TEACHER_FAILURE', {
        email,
        originalEmail,
        schoolId,
      })
  );
};

// This is called after submitting a form, so just one set of arguments.
export const inviteMultipleTeachers = (
  emailAddresses,
  schoolId,
  token,
  dispatch,
  state
) => {
  if (emailAddresses && emailAddresses.length) {
    emailAddresses.forEach((email) => {
      if (email.length) {
        inviteTeacher(email, null, schoolId, token, dispatch, state);
      }
    });
  }
};

export const clearInvitedTeachers = () => (dispatch) => {
  dispatch({
    type: 'CLEAR_INVITED_TEACHERS',
  });
};

export const clearAuthMessage = () => (dispatch) => {
  dispatch({
    type: 'CLEAR_AUTH_MESSAGE',
  });
};

export const checkInviteTokenValidity = (token) => (dispatch) => {
  return checkTemporaryTokenValidity(
    token,
    'INVITE',
    api.checkInviteToken,
    dispatch
  );
};

export const checkResetPasswordTokenValidity = (token) => (dispatch) => {
  return checkTemporaryTokenValidity(
    token,
    'RESET_PASSWORD',
    api.checkResetPasswordToken,
    dispatch
  );
};

const loginUser = (authInfo) =>
  api.postLogin(authInfo).then((response) => {
    console.log('trying again...');
    const { errorMsg, results } = response || {};
    if (errorMsg) {
      throw new Error(errorMsg);
    }
    const { token } = results;
    if (!token) {
      throw new Error('no token');
    }
    return token;
  });

const getUserOperation = (id, token) =>
  api.getUser(id, token).then((response) => {
    const { errorMsg } = response;
    if (errorMsg) {
      console.log('GOT GETUSER ERROR', errorMsg);
      throw new Error(response);
    }
    return response;
  });

export const postLoginWithRetries = ({ username, password } = {}) => async (
  dispatch,
  getState
) => {
  console.log('postLoginWithRetries called');
  dispatch({ type: 'POST_LOGIN_REQUEST' });
  const state = getState();
  if (!username || !password) return;
  /*  
    Repeatedly attempt to login - we need to do this because the API caches users, so we need
    to wait until the user is updated to be active
    TODO: can we fix this and update the chache immediatly when a user is reactivated?
  */
  const retries = 15;
  const delay = 3000;

  const token = await retryOperation(
    () => loginUser({ username, password }),
    delay,
    retries
  ).catch((e) => {
    console.log('postLogin Error', e);
    dispatch({ type: 'POST_LOGIN_FAILURE' });
  });

  if (!token) return;
  console.log('logged in success. token:', token);

  dispatch({ type: 'POST_LOGIN_SUCCESS', token });

  /* NOTE: this duplicates much logic from the `fetchUser` action. It is repeated here
        because we need the user to be fetched before the form is marked as a success */
  dispatch({ type: 'FETCH_ACTIVE_USER_REQUEST' });

  const id = 'me';

  const userResponse = await retryOperation(
    () => getUserOperation(id, token),
    delay,
    retries
  ).catch((response) => {
    handleError(dispatch, response, 'FETCH_ACTIVE_USER_FAILURE');
  });

  setTokenCookie(token);

  if (!userResponse || userResponse.errorMsg) {
    return;
  }

  const activeUserAction = receiveUsers(
    userResponse,
    'FETCH_ACTIVE_USER_SUCCESS'
  );
  dispatch(activeUserAction);
  const existingSchoolId = state.users.auth.schoolId;

  const newActiveUser =
    activeUserAction.response.entities.users[activeUserAction.response.result];
  const activeSchoolId = existingSchoolId || newActiveUser.roles[0][0];

  if (newActiveUser) {
    setSentryUser({
      id: newActiveUser.id,
      email: newActiveUser.auth_info.username,
      schoolId: activeSchoolId,
    });
  }
};
